import { compactObject, createRequestId } from '@wanjianshi/shared-utils';

const UTF8_HEADER_VALUE_PREFIX = 'utf8:';

/**
 * Encode arbitrary Unicode text into an ASCII-only HTTP header value.
 * The backend recognizes the `utf8:` prefix and decodes the percent-encoded payload.
 */
export function encodeUtf8HeaderValue(value: string): string {
  return `${UTF8_HEADER_VALUE_PREFIX}${encodeURIComponent(value)}`;
}

export class ApiError extends Error {
  public readonly status: number;
  public readonly code?: string | number;
  public readonly data?: unknown;
  public readonly requestId?: string;

  constructor(
    message: string,
    status: number,
    code?: string | number,
    data?: unknown,
    requestId?: string
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.data = data;
    this.requestId = requestId;
  }
}

export interface HttpErrorContext {
  url: string;
  method: string;
  error: ApiError;
}

export interface AuthAdapter {
  apply(init: RequestInit): Promise<RequestInit> | RequestInit;
  /**
   * Try to recover an expired authentication session. Returning true asks the
   * HTTP client to retry the original request once. Implementations should
   * deduplicate concurrent recovery calls when refresh tokens are rotated.
   */
  recoverUnauthorized?(context: HttpErrorContext): Promise<boolean> | boolean;
  onUnauthorized?(context: HttpErrorContext): Promise<void> | void;
  onForbidden?(context: HttpErrorContext): Promise<void> | void;
}

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  params?: Record<string, unknown>;
  timeoutMs?: number;
  responseType?: 'json' | 'text' | 'blob';
  skipUnauthorizedHandler?: boolean;
}

export interface HttpClientOptions {
  baseUrl?: string;
  timeoutMs?: number;
  auth: AuthAdapter;
  requestIdFactory?: () => string;
}

function buildUrl(baseUrl: string, path: string, params?: Record<string, unknown>): string {
  const raw = /^https?:\/\//i.test(path) ? path : `${baseUrl}${path.startsWith('/') ? path : `/${path}`}`;
  const url = new URL(raw, window.location.origin);
  Object.entries(compactObject(params ?? {})).forEach(([key, value]) => {
    if (Array.isArray(value)) value.forEach(item => url.searchParams.append(key, String(item)));
    else url.searchParams.set(key, String(value));
  });
  return url.origin === window.location.origin ? `${url.pathname}${url.search}` : url.toString();
}

async function parseResponse(response: Response, type: RequestOptions['responseType']) {
  if (response.status === 204) return undefined;
  if (type === 'blob') return response.blob();
  if (type === 'text') return response.text();
  const text = await response.text();
  if (!text) return undefined;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function formatErrorMessage(payload: unknown, fallback: string): string {
  if (!payload || typeof payload !== 'object') return fallback;
  const record = payload as Record<string, unknown>;
  const rawMessage = record.msg ?? record.message;
  if (typeof rawMessage === 'string' && rawMessage.trim()) return rawMessage;
  if (rawMessage && typeof rawMessage === 'object') {
    const message = (rawMessage as Record<string, unknown>).message;
    if (typeof message === 'string' && message.trim()) return message;
  }

  const detail = record.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (detail && typeof detail === 'object') {
    const detailMessage = (detail as Record<string, unknown>).message;
    if (typeof detailMessage === 'string' && detailMessage.trim()) return detailMessage;
  }
  if (Array.isArray(detail)) {
    const messages = detail.map(item => {
      if (!item || typeof item !== 'object') return String(item);
      const error = item as Record<string, unknown>;
      const location = Array.isArray(error.loc)
        ? error.loc.filter(value => value !== 'body').join('.')
        : '';
      const rawMessage = String(error.msg ?? '参数无效');
      const fieldLabels: Record<string, string> = {
        name: '规则名称',
        rule_type: '规则类型',
        scope_type: '生效范围',
        model_id: '模型',
        daily_free_limit: '会员每日免费次数',
        exclusive_credits: '专属积分数',
      };
      const field = location ? fieldLabels[location] ?? location : '';
      const message = rawMessage === 'String should have at most 128 characters'
        ? `${field || '文本'}不能超过 128 个字符`
        : rawMessage === 'Field required'
          ? `${field || '该字段'}不能为空`
          : rawMessage;
      return location && !message.startsWith(field) ? `${field || location}: ${message}` : message;
    }).filter(Boolean);
    if (messages.length) return `请求参数校验失败：${messages.join('；')}`;
  }
  return fallback;
}

export function createHttpClient(options: HttpClientOptions) {
  const baseUrl = (options.baseUrl ?? '').replace(/\/$/, '');
  const defaultTimeout = options.timeoutMs ?? 60_000;
  let unauthorizedRecoveryPromise: Promise<boolean> | null = null;
  let unauthorizedRecoveryGeneration = 0;
  let lastUnauthorizedRecoverySucceeded = false;

  function recoverUnauthorized(context: HttpErrorContext): Promise<boolean> {
    if (!options.auth.recoverUnauthorized) return Promise.resolve(false);
    if (!unauthorizedRecoveryPromise) {
      unauthorizedRecoveryPromise = Promise.resolve().then(
        () => options.auth.recoverUnauthorized?.(context) ?? false,
      ).then(recovered => {
        lastUnauthorizedRecoverySucceeded = recovered;
        unauthorizedRecoveryGeneration += 1;
        return recovered;
      }).catch(() => {
        lastUnauthorizedRecoverySucceeded = false;
        unauthorizedRecoveryGeneration += 1;
        return false;
      }).finally(() => {
        unauthorizedRecoveryPromise = null;
      });
    }
    return unauthorizedRecoveryPromise;
  }

  async function request<T>(
    path: string,
    requestOptions: RequestOptions = {},
    unauthorizedRetryCount = 0,
    recoveryGenerationAtStart = unauthorizedRecoveryGeneration,
  ): Promise<T> {
    const {
      params,
      timeoutMs = defaultTimeout,
      responseType = 'json',
      body,
      signal,
      headers: inputHeaders,
      skipUnauthorizedHandler = false,
      ...rest
    } = requestOptions;
    const url = buildUrl(baseUrl, path, params);
    const requestId = options.requestIdFactory?.() ?? createRequestId();
    const controller = new AbortController();
    const abort = () => controller.abort();
    if (signal?.aborted) controller.abort();
    else signal?.addEventListener('abort', abort, { once: true });
    const timeout = window.setTimeout(abort, timeoutMs);
    const headers = new Headers(inputHeaders);
    headers.set('X-Request-ID', requestId);
    if (body !== undefined && !(body instanceof FormData) && !headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }
    const init = await options.auth.apply({
      ...rest,
      headers,
      signal: controller.signal,
      body:
        body === undefined || body instanceof FormData || typeof body === 'string'
          ? (body as BodyInit | null | undefined)
          : JSON.stringify(body),
    });

    try {
      const response = await fetch(url, init);
      const payload = await parseResponse(response, responseType);
      if (!response.ok) {
        const record = payload && typeof payload === 'object' ? (payload as Record<string, unknown>) : {};
        const detail = record.detail;
        const detailCode = detail && typeof detail === 'object' && !Array.isArray(detail)
          ? (detail as Record<string, unknown>).code
          : undefined;
        const message = formatErrorMessage(payload, response.statusText || `HTTP ${response.status}`);
        const error = new ApiError(
          message || `HTTP ${response.status}`,
          response.status,
          (record.code ?? detailCode) as string | number | undefined,
          payload,
          response.headers.get('X-Request-ID') ?? requestId
        );
        const context = { url, method: String(init.method ?? 'GET'), error };
        if (response.status === 401 && !skipUnauthorizedHandler) {
          if (unauthorizedRetryCount === 0 && options.auth.recoverUnauthorized) {
            const recovered = recoveryGenerationAtStart !== unauthorizedRecoveryGeneration
              ? lastUnauthorizedRecoverySucceeded
              : await recoverUnauthorized(context);
            if (recovered) {
              if (controller.signal.aborted) {
                throw new ApiError('请求已取消或超时', 0, 'REQUEST_ABORTED', undefined, requestId);
              }
              return await request<T>(
                path,
                { ...requestOptions, signal: controller.signal },
                unauthorizedRetryCount + 1,
              );
            }
          }
          await options.auth.onUnauthorized?.(context);
        }
        if (response.status === 403) await options.auth.onForbidden?.(context);
        throw error;
      }
      return payload as T;
    } catch (error) {
      if (error instanceof ApiError) throw error;
      if (controller.signal.aborted) throw new ApiError('请求已取消或超时', 0, 'REQUEST_ABORTED', error, requestId);
      throw new ApiError(error instanceof Error ? error.message : '网络请求失败', 0, 'NETWORK_ERROR', error, requestId);
    } finally {
      window.clearTimeout(timeout);
      signal?.removeEventListener('abort', abort);
    }
  }

  return {
    request,
    get: <T>(path: string, opts?: RequestOptions) => request<T>(path, { ...opts, method: 'GET' }),
    post: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>(path, { ...opts, method: 'POST', body }),
    put: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>(path, { ...opts, method: 'PUT', body }),
    patch: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>(path, { ...opts, method: 'PATCH', body }),
    delete: <T>(path: string, opts?: RequestOptions) => request<T>(path, { ...opts, method: 'DELETE' }),
  };
}

export type HttpClient = ReturnType<typeof createHttpClient>;
